// API utility functions for eSanza Dashboard

import { API_URL } from '../api/config';

const API_BASE_URL = API_URL;
const DATA_INVALIDATION_EVENT = 'aaywa:data-invalidated';

type DataInvalidationDetail = {
    endpoint: string;
    method: string;
    timestamp: number;
};

/**
 * Get authentication token from localStorage
 * In production, this should come from a proper auth context
 */
export const getAuthToken = (): string | null => {
    return localStorage.getItem('aaywa_token');
};

/**
 * Generic API request wrapper with JWT authentication
 */
export async function apiRequest<T>(
    endpoint: string,
    options: RequestInit & { silent?: boolean } = {}
): Promise<T> {
    const { silent, ...fetchOptions } = options;
    const token = getAuthToken();

    const defaultHeaders: HeadersInit = {};

    // Only set Content-Type to JSON if body is NOT FormData
    if (!(options.body instanceof FormData)) {
        defaultHeaders['Content-Type'] = 'application/json';
    }

    if (token) {
        defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = {
        ...fetchOptions,
        headers: {
            ...defaultHeaders,
            ...fetchOptions.headers,
        },
    };

    try {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

        // Handle non-200 responses
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({
                message: response.statusText,
            }));

            const apiError = new Error(
                errorData.error || errorData.message || `API Error: ${response.status} ${response.statusText}`
            );
            Object.assign(apiError, errorData, { status: response.status });
            throw apiError;
        }

        // Parse JSON response
        const data = await response.json();

        // Emit an invalidation event for successful data mutations.
        const method = String(config.method || 'GET').toUpperCase();
        if (typeof window !== 'undefined' && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
            window.dispatchEvent(new CustomEvent<DataInvalidationDetail>(DATA_INVALIDATION_EVENT, {
                detail: {
                    endpoint,
                    method,
                    timestamp: Date.now()
                }
            }));
        }

        return data;
    } catch (error) {
        // Request cancellation is expected during navigation, refresh, and
        // React Strict Mode's development-only effect replay.
        if (error instanceof Error && error.name === 'AbortError') {
            throw error;
        }
        if (!silent) {
            console.error(`API Request Failed [${endpoint}]:`, error);
        }
        throw error;
    }
}

/**
 * GET request helper
 */
export async function apiGet<T>(endpoint: string, params?: Record<string, any>, init?: RequestInit & { silent?: boolean }): Promise<T> {
    let url = endpoint;
    if (params && Object.keys(params).length > 0) {
        const search = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && String(value) !== '') {
                search.set(key, String(value));
            }
        });
        const query = search.toString();
        if (query) url += `${endpoint.includes('?') ? '&' : '?'}${query}`;
    }
    return apiRequest<T>(url, { method: 'GET', ...init });
}

export async function apiDownload(endpoint: string, filename: string): Promise<void> {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.error || errorData.message || `Download failed: ${response.status}`);
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

/**
 * POST request helper
 */
export async function apiPost<T>(endpoint: string, body: any, init?: RequestInit): Promise<T> {
    const isFormData = body instanceof FormData;
    return apiRequest<T>(endpoint, {
        method: 'POST',
        body: isFormData ? body : JSON.stringify(body),
        ...init,
    });
}

/**
 * PATCH request helper
 */
export async function apiPatch<T>(endpoint: string, body: any, init?: RequestInit): Promise<T> {
    const isFormData = body instanceof FormData;
    return apiRequest<T>(endpoint, {
        method: 'PATCH',
        body: isFormData ? body : JSON.stringify(body),
        ...init,
    });
}

/**
 * PUT request helper
 */
export async function apiPut<T>(endpoint: string, body: any): Promise<T> {
    return apiRequest<T>(endpoint, {
        method: 'PUT',
        body: JSON.stringify(body),
    });
}

/**
 * DELETE request helper
 */
export async function apiDelete<T>(endpoint: string, init?: RequestInit): Promise<T> {
    return apiRequest<T>(endpoint, { method: 'DELETE', ...init });
}

/**
 * Upload file helper
 */
export async function apiUpload<T>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, any>
): Promise<T> {
    const token = getAuthToken();
    const formData = new FormData();
    formData.append('file', file);

    if (additionalData) {
        Object.entries(additionalData).forEach(([key, value]) => {
            formData.append(key, String(value));
        });
    }

    const headers: HeadersInit = {};
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    try {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: 'POST',
            headers,
            body: formData,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({
                message: response.statusText,
            }));
            throw new Error(errorData.message || `Upload failed: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error(`File Upload Failed [${endpoint}]:`, error);
        throw error;
    }
}

/**
 * Handle API errors with user-friendly messages
 */
export function handleApiError(error: any): string {
    if (error.message) {
        return error.message;
    }
    if (error.error) {
        return error.error;
    }
    if (typeof error === 'string') {
        return error;
    }
    return 'An unexpected error occurred. Please try again.';
}

export function subscribeToDataInvalidation(
    callback: (event: DataInvalidationDetail) => void,
    debounceMs = 350
): () => void {
    if (typeof window === 'undefined') {
        return () => { };
    }

    let timeoutId: number | undefined;

    const handler = (event: Event) => {
        const customEvent = event as CustomEvent<DataInvalidationDetail>;
        if (timeoutId) {
            window.clearTimeout(timeoutId);
        }
        timeoutId = window.setTimeout(() => {
            callback(customEvent.detail);
        }, debounceMs);
    };

    window.addEventListener(DATA_INVALIDATION_EVENT, handler as EventListener);

    return () => {
        if (timeoutId) {
            window.clearTimeout(timeoutId);
        }
        window.removeEventListener(DATA_INVALIDATION_EVENT, handler as EventListener);
    };
}
